// src/app/components/rating/rating.component.ts
import { Component, Input, Output, EventEmitter, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'; // For [(ngModel)]

@Component({
  selector: 'main-rating', // Your custom selector
  templateUrl: './app-rating.component.html',
  styleUrls: ['./app-rating.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppRatingComponent),
      multi: true,
    },
  ],
})
export class AppRatingComponent implements OnInit, ControlValueAccessor {
  // --- Inputs ---
  @Input() max: number = 5;
  @Input() readOnly: boolean = false;
  @Input() emptyStarIconName: string = 'star-outline';
  @Input() halfStarIconName: string = 'star-half';
  @Input() starIconName: string = 'star';

  // The actual rating value (internal)
  _value: number = 0;

  // --- Output for direct event handling (optional, but good practice) ---
  @Output() valueChange: EventEmitter<number> = new EventEmitter<number>();

  // --- ControlValueAccessor boilerplate ---
  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    if (value !== undefined && value !== null) {
      this._value = Math.max(0, Math.min(value, this.max)); // Ensure value is within bounds
    } else {
      this._value = 0; // Default to 0 if null/undefined
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.readOnly = isDisabled; // If disabled via form control, make it read-only
  }
  // --- End ControlValueAccessor boilerplate ---

  ngOnInit() {
    // You can add any initialization logic here if needed
  }

  /**
   * Handles click on a star to set the rating.
   * Only works if not in readOnly mode.
   * @param starIndex The index of the star clicked (1-based).
   * @param event The mouse event from the click.
   */
  setRating(starIndex: number, event?: MouseEvent): void {
    if (this.readOnly) {
      return;
    }

    // This logic allows for half-star selection if clicked roughly in the first half
    // of the star, otherwise full star. Adjust as needed for your UI.
    const clickX = event instanceof MouseEvent ? event.offsetX : 0; // Get X coordinate of click within the star element
    const starWidth = event?.target ? (event.target as HTMLElement).offsetWidth : 0; // Get width of the star element

    let newRating = starIndex;
    if (starWidth && clickX / starWidth < 0.5) {
      // If click is in the first half of the star
      newRating = starIndex - 0.5;
    }

    // If the same star is clicked again, reset to 0 (toggle off)
    if (newRating === this._value) {
      this._value = 0;
    } else {
      this._value = newRating;
    }

    // Inform Angular forms and emit to parent
    this.onChange(this._value);
    this.onTouched();
    this.valueChange.emit(this._value);
  }

  /**
   * Determines which icon to display for a given star index.
   * @param starIndex The 1-based index of the star.
   * @returns The name of the Ionic icon.
   */
  getStarIcon(starIndex: number): string {
    const currentRating = this._value;

    if (currentRating >= starIndex) {
      return this.starIconName; // Full star
    } else if (currentRating >= starIndex - 0.5) {
      return this.halfStarIconName; // Half star
    } else {
      return this.emptyStarIconName; // Empty star
    }
  }

  // Generate an array of numbers from 1 to max for *ngFor
  get starsArray(): number[] {
    return Array.from({ length: this.max }, (_, i) => i + 1);
  }
}
